import { useState } from 'react';
import { CheckCircle, XCircle, AlertCircle, ChevronRight, ChevronDown, GitBranch } from 'lucide-react';

// Type-based color classes matching mquery-game Datameerkat theme
const typeColors = {
  text: 'text-[#C2A528]',      // Yellow/Gold for strings
  number: 'text-[#2EA043]',    // Green for numbers
  logical: 'text-[#127373]',   // Teal for booleans
  null: 'text-[#414244]',      // Gray for null
  date: 'text-[#B5CEA8]',     // Light green for dates
  time: 'text-[#B5CEA8]',
  datetime: 'text-[#B5CEA8]',
  datetimezone: 'text-[#B5CEA8]',
  duration: 'text-[#B5CEA8]',
  function: 'text-[#AFB5BD]',  // Secondary for functions
  unknown: 'text-[#AFB5BD]',
  record: 'text-[#AFB5BD]',
  list: 'text-[#AFB5BD]',
  table: 'text-[#AFB5BD]',
};

function getTypeColor(type) {
  return typeColors[type] || typeColors.unknown;
}

function formatScalarDisplay(item) {
  if (!item) return 'null';
  if (item.kind === 'scalar') {
    if (item.type === 'text') return `"${item.value}"`;
    return item.value;
  }
  if (item.kind === 'list') return `{List: ${item.items.length} items}`;
  if (item.kind === 'record') return `[Record: ${item.fields.length} fields]`;
  if (item.kind === 'table') return `Table (${item.rows.length} rows, ${item.columns.length} columns)`;
  return String(item.value || '');
}

function getItemType(item) {
  if (!item) return 'null';
  if (item.kind === 'scalar') return item.type;
  return item.kind;
}

// Type display labels
const typeLabels = {
  text: 'ABC', number: '123', logical: 'T/F',
  date: 'Date', time: 'Time', datetime: 'DateTime',
  datetimezone: 'DateTZ', duration: 'Dur',
  null: 'Any', any: 'Any', unknown: 'Any',
  record: 'Rec', list: 'List', table: 'Tbl',
  function: 'Fn',
};

// Table renderer
function RenderTable({ data }) {
  const { columns, rows, columnTypes } = data;
  return (
    <div className="mt-2">
      <p className="text-xs text-[#687185] mb-1.5 font-medium">
        Table ({rows.length} rows, {columns.length} columns)
      </p>
      <div className="rounded-lg border border-[#2d3136] overflow-auto max-h-[240px]">
        <table className="w-full text-xs font-mono border-collapse">
          <thead className="sticky top-0 z-10">
            <tr className="bg-[#2d3136]">
              {columns.map((col, i) => {
                const colType = columnTypes?.[col] || 'any';
                return (
                  <th
                    key={i}
                    className="px-2.5 py-1.5 text-left border-r border-[#3a3f45] last:border-r-0 whitespace-nowrap"
                  >
                    <span className="text-game-text font-semibold">{col}</span>
                    <span className={`ml-1.5 text-[10px] font-normal opacity-60 ${getTypeColor(colType)}`}>
                      {typeLabels[colType] || 'Any'}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIdx) => (
              <tr
                key={rowIdx}
                className={rowIdx % 2 === 1 ? 'bg-[#1c1f23]' : 'bg-[#202327]'}
              >
                {row.map((cell, colIdx) => (
                  <td
                    key={colIdx}
                    className={`px-2.5 py-1 border-r border-[#2d3136] last:border-r-0 whitespace-nowrap ${getTypeColor(getItemType(cell))}`}
                  >
                    {formatScalarDisplay(cell)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Record renderer
function RenderRecord({ data }) {
  const { fields } = data;
  return (
    <div className="mt-2">
      <p className="text-xs text-[#687185] mb-1.5 font-medium">
        Record ({fields.length} fields)
      </p>
      <div className="rounded-lg border border-[#2d3136] overflow-auto max-h-[240px]">
        <table className="w-full text-xs font-mono border-collapse">
          <thead className="sticky top-0 z-10">
            <tr className="bg-[#2d3136]">
              <th className="px-2.5 py-1.5 text-left text-game-text font-semibold border-r border-[#3a3f45] w-[120px]">Field</th>
              <th className="px-2.5 py-1.5 text-left text-game-text font-semibold">Value</th>
            </tr>
          </thead>
          <tbody>
            {fields.map((field, idx) => (
              <tr
                key={idx}
                className={idx % 2 === 1 ? 'bg-[#1c1f23]' : 'bg-[#202327]'}
              >
                <td className="px-2.5 py-1 border-r border-[#2d3136] text-[#AFB5BD] whitespace-nowrap">
                  {field.name}
                </td>
                <td className={`px-2.5 py-1 whitespace-nowrap ${getTypeColor(getItemType(field.value))}`}>
                  {field.value.kind === 'table' ? (
                    <ExpandableValue item={field.value} preview={<span className="text-[#AFB5BD]">Table ({field.value.rows.length} rows, {field.value.columns.length} columns)</span>} />
                  ) : field.value.kind === 'record' ? (
                    <ExpandableValue item={field.value} preview={<span className="text-[#AFB5BD]">[Record: {field.value.fields.length} fields]</span>} />
                  ) : field.value.kind === 'list' ? (
                    <ExpandableValue item={field.value} preview={<span className="text-[#AFB5BD]">{'{'}List: {field.value.items.length} items{'}'}</span>} />
                  ) : (
                    formatScalarDisplay(field.value)
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// List renderer
function RenderList({ data }) {
  const { items } = data;
  return (
    <div className="mt-2">
      <p className="text-xs text-[#687185] mb-1.5 font-medium">
        List ({items.length} items)
      </p>
      <div className="rounded-lg border border-[#2d3136] overflow-auto max-h-[240px]">
        <table className="w-full text-xs font-mono border-collapse">
          <tbody>
            {items.map((item, idx) => (
              <tr
                key={idx}
                className={idx % 2 === 1 ? 'bg-[#1c1f23]' : 'bg-[#202327]'}
              >
                <td className="px-2 py-1 text-[#687185] border-r border-[#2d3136] w-[36px] text-right">
                  {idx}
                </td>
                <td className={`px-2.5 py-1 ${getTypeColor(getItemType(item))}`}>
                  {item.kind === 'record' ? (
                    <ExpandableValue item={item} preview={<RecordPreview record={item} />} />
                  ) : item.kind === 'list' ? (
                    <ExpandableValue item={item} preview={<ListPreview list={item} />} />
                  ) : item.kind === 'table' ? (
                    <ExpandableValue item={item} preview={<span className="text-[#AFB5BD]">Table ({item.rows.length} rows, {item.columns.length} columns)</span>} />
                  ) : (
                    formatScalarDisplay(item)
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Compact record preview for list items
function RecordPreview({ record }) {
  const fieldStrs = record.fields.slice(0, 3).map(f => {
    let vStr = formatScalarDisplay(f.value);
    if (vStr.length > 15) vStr = vStr.substring(0, 12) + '...';
    return `${f.name}: ${vStr}`;
  });
  if (record.fields.length > 3) fieldStrs.push('...');
  return <span className="text-[#AFB5BD]">[{fieldStrs.join(', ')}]</span>;
}

// Compact list preview for nested lists
function ListPreview({ list }) {
  if (list.items.length === 0) return <span className="text-[#AFB5BD]">{'{}'}</span>;
  const itemStrs = list.items.slice(0, 3).map(item => {
    let s = formatScalarDisplay(item);
    if (s.length > 12) s = s.substring(0, 9) + '...';
    return s;
  });
  if (list.items.length > 3) itemStrs.push('...');
  return <span className="text-[#AFB5BD]">{'{'}{itemStrs.join(', ')}{'}'}</span>;
}

// Expandable wrapper for nested lists/records/tables
function ExpandableValue({ item, preview }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="whitespace-normal">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1 hover:bg-[#2d3136]/50 rounded px-0.5 -mx-0.5 transition-colors cursor-pointer"
      >
        {expanded ? (
          <ChevronDown size={12} className="text-[#687185] flex-shrink-0" />
        ) : (
          <ChevronRight size={12} className="text-[#687185] flex-shrink-0" />
        )}
        <span className="whitespace-nowrap">{preview}</span>
      </button>
      {expanded && (
        <div className="mt-1 ml-0.5">
          {item.kind === 'record' && <RenderRecord data={item} />}
          {item.kind === 'list' && <RenderList data={item} />}
          {item.kind === 'table' && <RenderTable data={item} />}
        </div>
      )}
    </div>
  );
}

// Rich structured result renderer
function StructuredResult({ structured, result }) {
  if (!structured) {
    return <pre className="text-game-text mt-1 font-mono text-sm whitespace-pre-wrap">{result}</pre>;
  }

  switch (structured.kind) {
    case 'table':
      return <RenderTable data={structured} />;
    case 'record':
      return <RenderRecord data={structured} />;
    case 'list':
      return <RenderList data={structured} />;
    case 'scalar':
    default:
      return (
        <pre className={`mt-1 font-mono text-sm whitespace-pre-wrap ${getTypeColor(structured.type || 'unknown')}`}>
          {result}
        </pre>
      );
  }
}

export default function OutputPanel({
  result,
  structured,
  error,
  isRunning,
  isCorrect = null,
  expectedOutput,
  showExpected = false,
  onShowExecutionPlan,
  isTracing = false,
}) {
  const hasResult = result !== null && result !== undefined;
  const hasError = error !== null && error !== undefined;

  return (
    <div className="bg-game-surface rounded-2xl border border-[#2a2d31] overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 h-[46px] bg-[#2a2d31]/50 border-b border-[#2a2d31]">
        <span className="text-sm font-semibold text-game-text">Output</span>
        <div className="flex items-center gap-2">
          {isRunning && (
            <span className="text-xs text-game-primary animate-pulse-slow font-medium">Running...</span>
          )}
          {onShowExecutionPlan && (
            <button
              onClick={onShowExecutionPlan}
              disabled={isRunning || isTracing}
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium
                text-game-text-dim hover:text-game-text hover:bg-[#2a2d31]
                disabled:opacity-40 disabled:cursor-not-allowed
                transition-colors"
              title="Execution Plan"
            >
              <GitBranch size={14} />
              <span className="hidden sm:inline">Execution Plan</span>
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-4 min-h-[80px]">
        {isRunning && (
          <div className="flex items-center gap-2 text-game-text-dim">
            <div className="w-2 h-2 bg-game-primary rounded-full animate-pulse" />
            <span>Executing code...</span>
          </div>
        )}

        {!isRunning && hasError && (
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-game-error/20 flex items-center justify-center flex-shrink-0">
              <XCircle className="text-game-error" size={18} />
            </div>
            <div>
              <p className="text-game-error font-semibold">Error</p>
              <p className="text-game-text-dim mt-1 font-mono text-sm">{error}</p>
            </div>
          </div>
        )}

        {!isRunning && !hasError && hasResult && (
          <div className="flex items-start gap-3">
            {isCorrect === false ? (
              <div className="w-8 h-8 rounded-full bg-game-error/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <XCircle className="text-game-error" size={18} />
              </div>
            ) : (
              <div className="w-8 h-8 rounded-full bg-game-primary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <CheckCircle className="text-game-primary" size={18} />
              </div>
            )}
            <div className="flex-1 min-w-0">
              {isCorrect === false ? (
                <p className="text-game-error font-semibold">Incorrect</p>
              ) : (
                <p className="text-game-primary font-semibold">
                  {isCorrect === true ? 'Correct!' : 'Result'}
                </p>
              )}
              <StructuredResult structured={structured} result={result} />
            </div>
          </div>
        )}

        {!isRunning && !hasError && !hasResult && (
          <div className="flex items-center gap-3 text-game-text-dim">
            <AlertCircle size={18} />
            <span>Press Ctrl+Enter or click Run to execute your code</span>
          </div>
        )}
      </div>

      {/* Expected Output */}
      {showExpected && expectedOutput && (
        <div className="px-4 py-3 bg-[#2a2d31]/30 border-t border-[#2a2d31]">
          <p className="text-xs text-game-text-dim mb-1 font-medium">Expected Output:</p>
          <pre className="text-sm font-mono text-game-primary">{expectedOutput}</pre>
        </div>
      )}
    </div>
  );
}
